import React, { useState, useEffect, useCallback } from 'react';
import { ExternalLink, Download, AlertCircle } from 'lucide-react';

const VisualizadorPDF = ({ dataURL, nomeArquivo }) => {
  const [erroIframe, setErroIframe] = useState(false);
  const [erroEmbed, setErroEmbed] = useState(false);
  const [tentativaIframe, setTentativaIframe] = useState(true);
  const [timeoutIframe, setTimeoutIframe] = useState(false);
  const [iframeRef, setIframeRef] = useState(null);
  const [tentarBlobURL, setTentarBlobURL] = useState(false);
  const [blobURL, setBlobURL] = useState(null);

  useEffect(() => {
    // Resetar estados quando o dataURL mudar
    setErroIframe(false);
    setErroEmbed(false);
    setTentativaIframe(true);
    setTimeoutIframe(false);
    setTentarBlobURL(false);
    setBlobURL(null);
  }, [dataURL]);

  // Criar Blob URL quando necessário
  const criarBlobURL = useCallback((base64Data) => {
    try {
      // Remover o prefixo data:application/pdf;base64, se existir
      const base64 = base64Data.includes(',') ? base64Data.split(',')[1] : base64Data;

      // Converter base64 para bytes
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });

      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Erro ao criar Blob URL:', error);
      return dataURL; // Fallback para dataURL original
    }
  }, [dataURL]);

  useEffect(() => {
    if (tentarBlobURL && !blobURL) {
      const newBlobURL = criarBlobURL(dataURL);
      setBlobURL(newBlobURL);
    }
  }, [tentarBlobURL, dataURL, blobURL, criarBlobURL]);

  // Timeout para detectar se o iframe não carregou realmente
  useEffect(() => {
    if (tentativaIframe && !erroIframe) {
      const timer = setTimeout(() => {
        setTimeoutIframe(true);
        setTentativaIframe(false);
        setTentarBlobURL(true);
      }, 3000); // Reduzido de 5000 para 3000ms para melhor UX

      return () => clearTimeout(timer);
    }
  }, [tentativaIframe, erroIframe]);

  const verificarConteudoIframe = () => {
    if (iframeRef) {
      try {
        const iframeDoc = iframeRef.contentDocument || iframeRef.contentWindow?.document;
        if (iframeDoc) {
          const body = iframeDoc.body;
          if (body && body.innerHTML.trim() === '') {
            setErroIframe(true);
            setTentativaIframe(false);
            setTentarBlobURL(true);
          }
        }
      } catch (e) {
        // Cross-origin error - iframe carregou mas não conseguimos acessar o conteúdo
        setErroIframe(true);
        setTentativaIframe(false);
        setTentarBlobURL(true);
      }
    }
  };

  const handleIframeLoad = () => {
    // Verificar se realmente carregou após um pequeno delay
    setTimeout(verificarConteudoIframe, 1000);
  };

  const handleIframeError = () => {
    setErroIframe(true);
    setTentativaIframe(false);
    setTentarBlobURL(true);
  };

  const handleEmbedError = () => {
    setErroEmbed(true);
    setTentarBlobURL(true);
  };

  const handleBlobIframeError = () => {
    setTentarBlobURL(false);
  };

  const abrirNovaAba = () => {
    // Tentar usar Blob URL primeiro
    const blobURL = criarBlobURL(dataURL);
    window.open(blobURL, '_blank', 'noopener,noreferrer');
    
    // Limpar a URL do Blob após um tempo
    setTimeout(() => {
      if (blobURL !== dataURL) {
        URL.revokeObjectURL(blobURL);
      }
    }, 10000);
  };

  const baixarArquivo = () => {
    const blobURL = criarBlobURL(dataURL);
    const link = document.createElement('a');
    link.href = blobURL;
    link.download = nomeArquivo || 'documento.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Limpar a URL do Blob
    setTimeout(() => {
      if (blobURL !== dataURL) {
        URL.revokeObjectURL(blobURL);
      }
    }, 1000);
  };

  // Se todas as tentativas falharam
  if ((erroIframe || timeoutIframe) && erroEmbed && !tentarBlobURL) {
    return (
      <div className="w-full h-96 flex flex-col items-center justify-center bg-red-50 border border-red-200 rounded-lg">
        <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
        <h3 className="text-lg font-medium text-red-900 mb-2">
          Erro ao carregar anexo
        </h3>
        <p className="text-sm text-red-700 mb-4 text-center max-w-md">
          Não foi possível visualizar este PDF no navegador. 
          Tente baixar o arquivo para visualizá-lo.
        </p>
        <div className="flex space-x-3">
          <button
            onClick={abrirNovaAba}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-[color:var(--accent)] border border-transparent rounded-md hover:opacity-90"
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Abrir em Nova Aba
          </button>
          <button
            onClick={baixarArquivo}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700"
          >
            <Download className="h-4 w-4 mr-2" />
            Baixar PDF
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-full overflow-x-hidden">
      <div className="w-full h-96 max-h-[70vh] relative overflow-hidden">
        {/* Iframe - primeira tentativa */}
        {tentativaIframe && (
          <iframe
            ref={setIframeRef}
            src={dataURL}
            className="w-full h-full border rounded-lg"
            title={nomeArquivo}
            onLoad={handleIframeLoad}
            onError={handleIframeError}
            style={{ maxWidth: '100%' }}
          />
        )}

        {/* Iframe com Blob URL - segunda tentativa */}
        {tentarBlobURL && blobURL && !erroEmbed && (
          <iframe
            src={blobURL}
            className="w-full h-full border rounded-lg"
            title={nomeArquivo}
            onError={handleBlobIframeError}
            style={{ maxWidth: '100%' }}
          />
        )}

        {/* Embed - fallback final */}
        {(erroIframe || timeoutIframe) && !tentarBlobURL && !erroEmbed && (
          <embed
            src={dataURL}
            type="application/pdf"
            className="w-full h-full border rounded-lg"
            onError={handleEmbedError}
            style={{ maxWidth: '100%' }}
          />
        )}

        {/* Botões de ação opcionais - apenas se necessário */}
        {(erroIframe || timeoutIframe) && !tentarBlobURL && !erroEmbed && (
          <div className="mt-2 text-center">
            <p className="text-sm text-[color:var(--text-muted)] mb-2">
              Tentando carregar PDF...
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VisualizadorPDF;



